import React from 'react';
import { motion } from 'framer-motion';
import { 
  Eye, 
  Edit, 
  Copy, 
  Trash2, 
  BarChart3, 
  Clock, 
  Zap,
  TrendingUp,
  Users,
  Calendar,
  Star
} from 'lucide-react';

const TemplateLibrary = ({ 
  templates, 
  loading, 
  onPreview, 
  onEdit, 
  onDuplicate, 
  onDelete, 
  onAnalyze,
  getTypeIcon 
}) => {

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      'email_campaigns': 'bg-blue-100 text-blue-800',
      'sms_messages': 'bg-green-100 text-green-800',
      'social_media_posts': 'bg-purple-100 text-purple-800',
      'sales_scripts': 'bg-orange-100 text-orange-800',
      'lead_outreach': 'bg-red-100 text-red-800',
      'follow_up_sequences': 'bg-indigo-100 text-indigo-800',
      'promotional_content': 'bg-pink-100 text-pink-800',
      'customer_support': 'bg-gray-100 text-gray-800'
    };
    return colors?.[category] || 'bg-gray-100 text-gray-800';
  };

  const getUsageData = (template) => {
    const analytics = template?.template_analytics?.[0];
    return {
      usageCount: analytics?.usage_count || 0,
      lastUsed: analytics?.last_used_at ? formatDate(analytics?.last_used_at) : 'Never',
      performance: analytics?.performance_data || {}
    };
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)]?.map((_, index) => (
          <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 animate-pulse">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
            <div className="space-y-2 mb-4">
              <div className="h-3 bg-gray-200 rounded"></div>
              <div className="h-3 bg-gray-200 rounded w-5/6"></div>
              <div className="h-3 bg-gray-200 rounded w-4/6"></div>
            </div>
            <div className="flex justify-between items-center">
              <div className="h-6 bg-gray-200 rounded-full w-20"></div>
              <div className="flex gap-2">
                <div className="h-8 w-8 bg-gray-200 rounded"></div>
                <div className="h-8 w-8 bg-gray-200 rounded"></div>
                <div className="h-8 w-8 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (templates?.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <Star className="h-12 w-12 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
        <p className="text-gray-500 mb-6">
          Create your first template or adjust your search filters to see more results.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {templates?.map((template, index) => {
        const usageData = getUsageData(template);
        
        return (
          <motion.div
            key={template?.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200 overflow-hidden"
          >
            {/* Header */}
            <div className="p-6 pb-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1.5 bg-gray-100 rounded">
                      {getTypeIcon(template?.type)}
                    </div>
                    {template?.ai_generated && (
                      <div className="p-1 bg-gradient-to-r from-purple-500 to-blue-500 rounded">
                        <Zap className="h-3 w-3 text-white" />
                      </div>
                    )}
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getCategoryColor(template?.category)}`}>
                      {template?.category?.replace('_', ' ')}
                    </span>
                  </div>
                  <h3 className="font-medium text-gray-900 truncate mb-1">{template?.name}</h3>
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(template?.updated_at)}
                  </p>
                </div>
              </div>

              {/* Content Preview */}
              <div className="mb-4">
                {template?.subject && (
                  <div className="text-xs font-medium text-gray-600 mb-1">Subject:</div>
                )}
                <p className="text-sm text-gray-600 line-clamp-3">
                  {template?.subject && (
                    <span className="font-medium text-gray-900">{template?.subject}</span>
                  )}
                  {template?.subject && <br />}
                  {template?.content?.length > 100 
                    ? `${template?.content?.substring(0, 100)}...` 
                    : template?.content
                  }
                </p>
              </div>

              {/* Tags */}
              {template?.tags && template?.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-4">
                  {template?.tags?.slice(0, 3)?.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                  {template?.tags?.length > 3 && (
                    <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                      +{template?.tags?.length - 3}
                    </span>
                  )}
                </div>
              )}

              {/* Performance Stats */}
              <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {usageData?.usageCount} uses
                  </span>
                  {usageData?.performance?.open_rate && (
                    <span className="flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      {Math.round(usageData?.performance?.open_rate * 100)}% open
                    </span>
                  )}
                </div>
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {usageData?.lastUsed}
                </span>
              </div>
            </div>
            {/* Actions */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onPreview?.(template)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors"
                    title="Preview"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onEdit?.(template)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onDuplicate?.(template)}
                    className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Duplicate"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onAnalyze?.(template)}
                    className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                    title="Analytics"
                  >
                    <BarChart3 className="h-4 w-4" />
                  </button>
                </div>
                <button
                  onClick={() => onDelete?.(template?.id)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default TemplateLibrary;